import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Droplets, Loader2, PackageOpen, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { CartDrawer } from "@/components/CartDrawer";
import { Footer } from "@/components/Footer";
import { useAuth, formatApiError } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: "orbs-refill",
    icon: Droplets,
    color: "#00E5FF",
    name: "Orbs Refill",
    price: "€9,95",
    tagline: "Nooit meer zonder munitie",
    features: [
      "20.000 verse gel orbs, elke maand",
      "Wisselende kleurenmix (lime, cyan, magenta)",
      "Gratis verzending, elke maand",
      "Maandelijks opzegbaar",
    ],
  },
  {
    id: "mystery-pack",
    icon: PackageOpen,
    color: "#FF087B",
    name: "Mystery Pack",
    price: "€19,95",
    tagline: "Elke maand een verrassing",
    popular: true,
    features: [
      "20.000 gel orbs + mystery accessoire",
      "Wisselende items: magazijnen, skins, targets & gear",
      "Exclusieve ledendrops die nergens anders te koop zijn",
      "Gratis verzending, maandelijks opzegbaar",
    ],
  },
];

const PlanCard = ({ plan, index }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const subscribe = async () => {
    if (!user) {
      toast("Log eerst in of maak een account aan", { description: "Daarna rond je je abonnement in 1 minuut af." });
      navigate("/account");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/payments/subscribe`,
        { plan: plan.id, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <motion.div
      data-testid={`plan-card-${plan.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -8 }}
      className={`relative bg-slate-900/80 backdrop-blur-xl rounded-3xl p-8 border transition-colors duration-300 ${
        plan.popular ? "border-[#FF087B]/60 shadow-[0_0_50px_rgba(255,8,123,0.15)]" : "border-slate-800 hover:border-[#00E5FF]/40"
      }`}
    >
      {plan.popular && (
        <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#FF087B] to-[#00E5FF] text-white text-xs font-extrabold uppercase tracking-widest px-4 py-1.5 rounded-full flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> Meest gekozen
        </span>
      )}

      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
        style={{ backgroundColor: `${plan.color}1A`, border: `1px solid ${plan.color}44` }}
      >
        <plan.icon className="w-7 h-7" style={{ color: plan.color }} />
      </div>

      <h2 className="font-display text-4xl uppercase tracking-wide text-white" data-testid={`plan-name-${plan.id}`}>
        {plan.name}
      </h2>
      <p className="text-sm text-slate-400 mt-1">{plan.tagline}</p>

      <div className="mt-6 flex items-end gap-1.5">
        <span className="text-5xl font-extrabold text-white" data-testid={`plan-price-${plan.id}`}>{plan.price}</span>
        <span className="text-slate-500 text-sm mb-1.5">/maand</span>
      </div>

      <ul className="mt-7 space-y-3">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
            <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: plan.color }} strokeWidth={3} />
            {f}
          </li>
        ))}
      </ul>

      <button
        data-testid={`subscribe-button-${plan.id}`}
        onClick={subscribe}
        disabled={busy}
        className={`mt-8 w-full font-extrabold uppercase tracking-wider py-4 rounded-full transition-all duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2 ${
          plan.popular
            ? "bg-gradient-to-r from-[#FF087B] to-[#00E5FF] text-white shadow-[0_0_25px_rgba(255,8,123,0.35)]"
            : "bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 glow-lime"
        }`}
      >
        {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Even geduld...</>) : "Start abonnement"}
      </button>
    </motion.div>
  );
};

export default function Subscriptions() {
  return (
    <div data-testid="subscriptions-page" className="bg-[#090D16] text-slate-100 min-h-screen">
      <div className="noise-overlay" />
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <p className="font-mono-brand text-xs uppercase tracking-widest text-[#00FF66] mb-4">Gelvaro Club</p>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl uppercase tracking-wide leading-[0.9]">
            <span className="text-white">Elke maand</span><br />
            <span className="text-gradient-splat">verse orbs</span>
          </h1>
          <p className="mt-6 text-slate-400 leading-relaxed">
            Kies je maandelijkse drop: pure munitie met de Orbs Refill, of ga all-in met het Mystery Pack vol verrassende gear. Opzeggen kan altijd.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {PLANS.map((p, i) => (
            <PlanCard key={p.id} plan={p} index={i} />
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center text-xs text-slate-500 mt-12"
        >
          Betaling via Stripe (iDEAL, Bancontact, creditcard). Je abonnement beheer je vanuit je account.
        </motion.p>
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
